import React from 'react';
import { ArrowDropUp, ArrowRight, AddLocation } from '@mui/icons-material';
import { Mail, Phone, Facebook, Linkedin, Instagram, Youtube } from 'lucide-react';

export default function Footer() {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-gradient-to-b from-gray-900 to-black text-gray-100 font-[Lato]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-12">

          {/* ABOUT US */}
          <div className="lg:col-span-5">
            <div className="mb-4">
              <h3 className="text-xl font-bold text-white tracking-wide mb-1">
                Techno Gyan Computer Institute
              </h3>
              <div className="w-16 h-1 bg-gradient-to-r from-orange-500 to-orange-400 rounded-full"></div>
            </div>
            
            <p className="text-sm leading-relaxed text-gray-300 mb-4">
              TGCI is dedicated to fostering technological proficiency and innovation
              through comprehensive computer education. With a strong focus on
              practical learning and real-world applications, we prepare students
              for successful careers in technology and beyond.
            </p>

            <a
              href="/about"
              className="inline-flex items-center gap-2 text-sm font-medium text-orange-400 hover:text-orange-300 transition-colors group"
            >
              Learn More About Us 
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </a>

            {/* Social Media */}
            <div className="mt-6">
              <p className="text-xs text-gray-400 mb-3 uppercase tracking-wider">Connect With Us</p>
              <div className="flex gap-3">
                <a
                  href="https://www.instagram.com/technogyan_institute_dehradun"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full bg-gray-800 hover:bg-orange-500 flex items-center justify-center transition-all duration-300 hover:scale-110"
                  title="Follow us on Instagram"
                >
                  <Instagram size={16} />
                </a>
                <a
                  href="https://www.facebook.com/share/1AUB82Ka1k/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full bg-gray-800 hover:bg-orange-500 flex items-center justify-center transition-all duration-300 hover:scale-110"
                  title="Follow us on Facebook"
                >
                  <Facebook size={16} />
                </a>
                <a
                  href="https://youtube.com/@technogyanenglish?si=1MioGuB_DBxFKHaj"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full bg-gray-800 hover:bg-orange-500 flex items-center justify-center transition-all duration-300 hover:scale-110"
                  title="Subscribe on YouTube"
                >
                  <Youtube size={16} />
                </a>
                <a
                  href="https://www.linkedin.com/company/techno-gyan-computer-institute"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full bg-gray-800 hover:bg-orange-500 flex items-center justify-center transition-all duration-300 hover:scale-110"
                  title="Connect on LinkedIn"
                >
                  <Linkedin size={16} />
                </a>
              </div>
            </div>
          </div>

          {/* CONTACT US */}
          <div className="lg:col-span-4">
            <div className="mb-4">
              <h3 className="text-xl font-bold text-white tracking-wide mb-1">Get In Touch</h3>
              <div className="w-16 h-1 bg-gradient-to-r from-orange-500 to-orange-400 rounded-full"></div>
            </div>

            <div className="space-y-4">
              <a 
                href="tel:+917533839843"
                className="flex items-center gap-3 text-sm text-gray-300 hover:text-orange-400 transition-colors group"
              >
                <div className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center group-hover:bg-orange-500/10 transition-colors">
                  <Phone size={18} className="group-hover:text-orange-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Phone</p>
                  <p className="font-medium">+91 7533839843</p>
                </div>
              </a>

              <a 
                href="mailto:tgci.ac.in@gmail.com"
                className="flex items-center gap-3 text-sm text-gray-300 hover:text-orange-400 transition-colors group"
              >
                <div className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center group-hover:bg-orange-500/10 transition-colors">
                  <Mail size={18} className="group-hover:text-orange-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Email</p>
                  <p className="font-medium break-all">tgci.ac.in@gmail.com</p>
                </div>
              </a>

              <a
                href="https://maps.google.com/?q=Techno+Gyan+Computer+Institute+Dehradun"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-3 text-sm text-gray-300 hover:text-orange-400 transition-colors group"
              >
                <div className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center flex-shrink-0 group-hover:bg-orange-500/10 transition-colors">
                  <AddLocation fontSize="small" className="group-hover:text-orange-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Address</p>
                  <p className="leading-relaxed">
                    Opp. DAV PG College,<br/>
                    Karanpur, Dehradun – 248001
                  </p>
                </div>
              </a>
            </div>
          </div>

          {/* QUICK LINKS */}
          <div className="lg:col-span-3">
            <div className="mb-4">
              <h3 className="text-xl font-bold text-white tracking-wide mb-1">Quick Links</h3>
              <div className="w-16 h-1 bg-gradient-to-r from-orange-500 to-orange-400 rounded-full"></div>
            </div>

            <ul className="space-y-3">
              {[
                { label: 'Home', href: '/' },
                { label: 'Courses', href: '/courses' },
                { label: 'About Us', href: '/about' },
                { label: 'Contact', href: '/contact' },
                { label: 'Admissions', href: '/admissions' },
              ].map((link, i) => (
                <li key={i}>
                  <a
                    href={link.href}
                    className="text-sm text-gray-300 hover:text-orange-400 transition-colors inline-flex items-center gap-2 group"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-600 group-hover:bg-orange-400 transition-colors"></span>
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>

            <button
              onClick={scrollToTop}
              className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-orange-400 hover:text-orange-300 transition-colors group"
            >
              <ArrowDropUp className="group-hover:-translate-y-1 transition-transform" />
              Back to Top
            </button>
          </div>

        </div>
      </div>

      {/* BOTTOM BAR */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-gray-400">
            <p>
              © {new Date().getFullYear()} Techno Gyan Computer Institute (TGCI). All rights reserved.
            </p>
            <div className="flex gap-6">
              <a href="/privacy" className="hover:text-orange-400 transition-colors">Privacy Policy</a>
              <a href="/terms" className="hover:text-orange-400 transition-colors">Terms of Service</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}